import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';
import { UtilsService } from 'src/app/services/utils.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { NgClass } from '@angular/common';
import { CommercialDashboardMetrics, PotentialClientOrigin } from 'src/app/models/potential-client.interface';

type MetricKey = keyof CommercialDashboardMetrics;
type MetricFormat = 'number' | 'ratio' | 'money';

interface DashboardMetricRow {
  key: MetricKey;
  label: string;
  group: 'setter' | 'closer' | 'ratios';
  format: MetricFormat;
}

interface DashboardQueryParams {
  from: string;
  to: string;
  commercial_ids: string | null;
  origin_ids: string | null;
}

@Component({
  selector: 'app-commercial-dashboard',
  templateUrl: './commercial-dashboard.component.html',
  styleUrls: ['./commercial-dashboard.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatInputModule,
    NgClass,
  ],
})
export class CommercialDashboardComponent implements OnInit {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  filterForm: UntypedFormGroup;
  private formPersistence: DashboardQueryParams | null = null;

  summary: CommercialDashboardMetrics | null = null;
  days: CommercialDashboardMetrics[] = [];
  commercials: { id: number; name: string; surnames: string }[] = [];
  origins: PotentialClientOrigin[] = [];

  readonly metricRows: DashboardMetricRow[] = [
    { key: 'leads_assigned', label: 'Leads asignados', group: 'setter', format: 'number' },
    { key: 'leads_contacted', label: 'Leads contactados', group: 'setter', format: 'number' },
    { key: 'leads_qualified', label: 'Leads que califican', group: 'setter', format: 'number' },
    { key: 'leads_not_qualified', label: 'Leads que no califican', group: 'setter', format: 'number' },
    { key: 'leads_scheduled', label: 'Leads agendados', group: 'setter', format: 'number' },
    { key: 'calls_scheduled', label: 'Llamadas agendadas', group: 'closer', format: 'number' },
    { key: 'calls_net', label: 'Resultado (agend. − reprog.)', group: 'closer', format: 'number' },
    { key: 'calls_done', label: 'Llamadas realizadas', group: 'closer', format: 'number' },
    { key: 'offers_presented', label: 'Ofertas presentadas', group: 'closer', format: 'number' },
    { key: 'closes', label: 'Cierres', group: 'closer', format: 'number' },
    { key: 'ratio_lead_to_scheduled', label: '% Lead → Agendado', group: 'ratios', format: 'ratio' },
    { key: 'ratio_call_to_offer', label: '% Llamada → Oferta', group: 'ratios', format: 'ratio' },
    { key: 'ratio_offer_to_close', label: '% Oferta → Cierre', group: 'ratios', format: 'ratio' },
    { key: 'ratio_call_to_close', label: '% Llamada → Cierre', group: 'ratios', format: 'ratio' },
    { key: 'ratio_lead_to_close', label: '% Lead → Cierre', group: 'ratios', format: 'ratio' },
    { key: 'revenue_per_lead', label: '€ Facturado / Lead', group: 'ratios', format: 'money' },
    { key: 'revenue_per_close', label: '€ Facturado / Cierre', group: 'ratios', format: 'money' },
    { key: 'cash_collect_ratio', label: '% Cash collect / Facturado', group: 'ratios', format: 'ratio' },
  ];

  private api = inject(ApiService);
  private fb = inject(UntypedFormBuilder);
  private activatedRoute = inject(ActivatedRoute);
  private utils = inject(UtilsService);
  private router = inject(Router);

  constructor() {
    const fromParam = this.activatedRoute.snapshot.queryParamMap.get('from');
    const toParam = this.activatedRoute.snapshot.queryParamMap.get('to');
    const commercialParam = this.activatedRoute.snapshot.queryParamMap.get('commercial_ids');
    const originParam =
      this.activatedRoute.snapshot.queryParamMap.get('origin_ids') ?? this.activatedRoute.snapshot.queryParamMap.get('origin_id');

    this.filterForm = this.fb.group({
      from: [fromParam ? this.utils.stringToDate(fromParam) : this.getMonthStart(), Validators.required],
      to: [toParam ? this.utils.stringToDate(toParam) : this.getMonthEnd(), Validators.required],
      commercial_ids: [this.parseIdList(commercialParam)],
      origin_ids: [this.parseIdList(originParam)],
    });
  }

  ngOnInit(): void {
    this.api.getPotentialClientOrigins().subscribe(data => {
      this.origins = Array.isArray(data?.data) ? data.data : [];
      this.applyDefaultFiltersIfNeeded();
    });
    this.initFilterFormListener();
    this.listenQueryParameters();
  }

  get setterRows() {
    return this.metricRows.filter(r => r.group === 'setter');
  }

  get closerRows() {
    return this.metricRows.filter(r => r.group === 'closer');
  }

  get ratioRows() {
    return this.metricRows.filter(r => r.group === 'ratios');
  }

  get metaAdStatusColumns() {
    return this.summary?.leads_by_meta_ad?.[0]?.by_status ?? [];
  }

  shortStatusLabel(label: string): string {
    const map: Record<string, string> = {
      'Por contactar': 'Contactar',
      Contactado: 'Contact.',
      Califica: 'Califica',
      'Reunión agendada': 'Agendada',
      'Primera reunión hecha': '1ª reun.',
      'Reunión de cierre hecha': 'Cierre',
      Cerrado: 'Cerrado',
      'No cerrado': 'No cerr.',
      Rechazado: 'Rechaz.',
    };
    return map[label] ?? label;
  }

  dayLabel(date: string | null): string {
    if (!date) return '';
    const d = new Date(date + 'T12:00:00');
    return d.getDate().toString();
  }

  dayWeekday(date: string | null): string {
    if (!date) return '';
    const d = new Date(date + 'T12:00:00');
    return d.toLocaleDateString('es-ES', { weekday: 'short' }).replace('.', '');
  }

  cellValue(row: DashboardMetricRow, day: CommercialDashboardMetrics): string {
    const raw = day[row.key];
    if (row.format === 'ratio') return this.formatRatio(raw as number | null);
    if (row.format === 'money') return this.formatMoney(raw as number | null);
    return String(raw ?? 0);
  }

  summaryValue(row: DashboardMetricRow): string {
    if (!this.summary) return '—';
    return this.cellValue(row, this.summary);
  }

  heatClass(row: DashboardMetricRow, day: CommercialDashboardMetrics): string {
    if (row.format !== 'number') return '';
    const value = Number(day[row.key] ?? 0);
    if (value <= 0) return 'heat-0';
    const max = Math.max(...this.days.map(d => Number(d[row.key] ?? 0)), 1);
    const ratio = value / max;
    if (ratio >= 0.75) return 'heat-3';
    if (ratio >= 0.4) return 'heat-2';
    return 'heat-1';
  }

  isToday(date: string | null): boolean {
    if (!date) return false;
    const today = new Date();
    const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    return date === iso;
  }

  isWeekend(date: string | null): boolean {
    if (!date) return false;
    const d = new Date(date + 'T12:00:00').getDay();
    return d === 0 || d === 6;
  }

  formatRatio(value: number | null | undefined): string {
    if (value == null) return '—';
    return `${value}%`;
  }

  formatMoney(value: number | null | undefined): string {
    if (value == null) return '—';
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
  }

  private initFilterFormListener() {
    this.filterForm.valueChanges.pipe(debounceTime(200)).subscribe(data => {
      if (!data.from || !data.to) {
        return;
      }

      const params = this.toQueryParams(data);
      if (JSON.stringify(params) === JSON.stringify(this.formPersistence)) {
        return;
      }

      this.formPersistence = params;
      this.router.navigate([], { queryParams: params });
    });
  }

  private listenQueryParameters() {
    this.activatedRoute.queryParams.subscribe(params => {
      const normalized = this.normalizeRouteParams(params);
      if (JSON.stringify(normalized) !== JSON.stringify(this.formPersistence)) {
        this.formPersistence = normalized;

        const patch: Record<string, unknown> = {
          from: this.utils.stringToDate(normalized.from),
          to: this.utils.stringToDate(normalized.to),
        };

        // Solo sobrescribir selects si vienen en la URL; si no, se aplican defaults (todos).
        if (params['commercial_ids'] != null) {
          patch['commercial_ids'] = this.parseIdList(params['commercial_ids']);
        }
        if (params['origin_ids'] != null || params['origin_id'] != null) {
          patch['origin_ids'] = this.parseIdList(normalized.origin_ids);
        }

        this.filterForm.patchValue(patch, { emitEvent: false });
      } else if (this.formPersistence == null) {
        this.formPersistence = normalized;
      }

      this.fetchKpis();
    });
  }

  private fetchKpis() {
    const from = this.filterForm.value.from;
    const to = this.filterForm.value.to;
    if (!from || !to) return;

    this.api
      .getPotentialClientsKpis(from, to, {
        commercial_ids: this.filterForm.value.commercial_ids,
        origin_ids: this.filterForm.value.origin_ids,
      })
      .subscribe(data => {
        this.summary = data.summary;
        this.days = data.days ?? [];
        this.commercials = data.commercials ?? [];
        this.applyDefaultFiltersIfNeeded();
      });
  }

  /** Mes en curso + todos los comerciales/orígenes si la URL no fija esos filtros. */
  private applyDefaultFiltersIfNeeded() {
    const query = this.activatedRoute.snapshot.queryParamMap;
    const updates: { commercial_ids?: number[]; origin_ids?: number[] } = {};

    if (!query.has('commercial_ids') && this.commercials.length > 0) {
      const allIds = this.commercials.map(c => c.id);
      if (!this.sameIdSet(this.filterForm.value.commercial_ids, allIds)) {
        updates.commercial_ids = allIds;
      }
    }

    if (!query.has('origin_ids') && !query.has('origin_id') && this.origins.length > 0) {
      const allIds = this.origins.map(o => o.id);
      if (!this.sameIdSet(this.filterForm.value.origin_ids, allIds)) {
        updates.origin_ids = allIds;
      }
    }

    if (Object.keys(updates).length > 0) {
      this.filterForm.patchValue(updates);
    }
  }

  private toQueryParams(data: { from: Date; to: Date; commercial_ids?: number[]; origin_ids?: number[] }): DashboardQueryParams {
    return {
      from: this.utils.dateToString(new Date(data.from)),
      to: this.utils.dateToString(new Date(data.to)),
      commercial_ids: data.commercial_ids?.length ? data.commercial_ids.join(',') : null,
      origin_ids: data.origin_ids?.length ? data.origin_ids.join(',') : null,
    };
  }

  private normalizeRouteParams(params: Record<string, string>): DashboardQueryParams {
    const legacyOrigin = params['origin_id'] ? String(params['origin_id']) : null;
    return {
      from: params['from'] ? String(params['from']) : this.utils.dateToString(this.getMonthStart()),
      to: params['to'] ? String(params['to']) : this.utils.dateToString(this.getMonthEnd()),
      commercial_ids: params['commercial_ids'] != null ? String(params['commercial_ids']) : null,
      origin_ids: params['origin_ids'] != null ? String(params['origin_ids']) : legacyOrigin,
    };
  }

  private parseIdList(value: string | null | undefined): number[] {
    if (!value) return [];
    return String(value)
      .split(',')
      .map(Number)
      .filter(id => !Number.isNaN(id) && id > 0);
  }

  private sameIdSet(a: number[] | null | undefined, b: number[]): boolean {
    const left = [...(a ?? [])].map(Number).sort((x, y) => x - y);
    const right = [...b].map(Number).sort((x, y) => x - y);
    return JSON.stringify(left) === JSON.stringify(right);
  }

  private getMonthStart(now = new Date()) {
    return new Date(now.getFullYear(), now.getMonth(), 1);
  }

  private getMonthEnd(now = new Date()) {
    return new Date(now.getFullYear(), now.getMonth() + 1, 0);
  }
}
